<script setup lang="ts">
import {
  ChangelogServices,
  SalesDataManagerServices,
} from '@appinternal/karyawan-api-services';
import { computed, inject, ref, shallowRef } from 'vue';

import ButtonDownload from 'lib/components/buttondownload/ButtonDownload.vue';
import ButtonFilter from 'lib/components/buttonfilter/ButtonFilter.vue';
import ButtonSearch from 'lib/components/buttonsearch/ButtonSearch.vue';
import DataTable from 'lib/components/datatable/DataTable.vue';
import {
  FetchListResponse,
  TableCellComponent,
  TableColumn,
} from 'lib/components/datatable/DataTable.vue.d';
import TooltipSpan from 'lib/components/tooltipspan/TooltipSpan.vue';

import ChangelogFilter from './ChangelogFilter.vue';
import {
  ChangelogFilterQuery,
  ChangelogPageEmits,
  ChangelogPageProps,
  ChangelogType,
} from './ChangelogPage.vue.d';
import { formatISODate, useToast } from '../../utils';

const props = withDefaults(defineProps<ChangelogPageProps>(), {
  useButtonDownload: false,
});
defineEmits<ChangelogPageEmits>();
const toast = useToast();
const Preset = inject<Record<string, any>>('preset', {}).changelog;

const dataTableKey = shallowRef<number>(0);
const showChangelogFilter = shallowRef<boolean>(false);

/**
 * Define the default params
 */
const defaultParamsQuery = ref<ChangelogFilterQuery>({
  object: props.object,
  ...props.customParams,
});

const scrollHeight = computed<string | undefined>(() => {
  if (props.isDialog && showChangelogFilter.value) {
    return 'calc(57vh - 126px)';
  } else if (props.isDialog) return '57vh';
  return undefined;
});

const tableName = computed<string>(() => {
  return (
    props.tableName ??
    `changelog-page-${defaultParamsQuery.value.object?.toLowerCase().split(' ').join('-')}`
  );
});

const changelogColumn = ((): TableColumn[] => {
  const tempColumns = [
    {
      field: 'createdAt',
      header: 'Tanggal',
      sortable: true,
      visible: !props.removedColumns?.includes('createdAt'),
      bodyTemplate: (data: ChangelogType): string => {
        return data.createdAt ? formatISODate(data.createdAt) : '-';
      },
    },
    {
      field: 'action',
      header: 'Aksi',
      sortable: true,
      fixed: true,
      visible: !props.removedColumns?.includes('action'),
      bodyTemplate: (data: ChangelogType): string => {
        return data.action ?? '-';
      },
    },
    {
      field: 'objectName',
      header: props.objectNameColumn ?? props.object,
      sortable: true,
      fixed: true,
      visible:
        !props.additionalTemplateColumns?.length &&
        !props.removedColumns?.includes('objectName'),
      bodyTemplate: (data: ChangelogType): string => {
        return data.objectName ?? '-';
      },
    },
    {
      field: 'field',
      header: 'Field',
      sortable: true,
      fixed: true,
      visible:
        !/Testing/.test(props.object) &&
        !props.removedColumns?.includes('field'),
      bodyTemplate: (data: ChangelogType): string => {
        return data.field ?? '-';
      },
    },
    {
      field: 'oldValue',
      header: 'Data Lama',
      sortable: true,
      visible: !props.removedColumns?.includes('oldValue'),
      bodyComponent: (data: ChangelogType): TableCellComponent => {
        return {
          component: TooltipSpan,
          props: { fullText: data.oldValue ?? '-' },
        };
      },
    },
    {
      field: 'newValue',
      header: 'Data Baru',
      sortable: true,
      visible: !props.removedColumns?.includes('newValue'),
      bodyComponent: (data: ChangelogType): TableCellComponent => {
        return {
          component: TooltipSpan,
          props: { fullText: data.newValue ?? '-' },
        };
      },
    },
    {
      field: 'modifiedBy',
      header: 'Diubah Oleh',
      sortable: true,
      visible: !props.removedColumns?.includes('modifiedBy'),
      bodyTemplate: (data: ChangelogType): string => {
        return data.modifiedBy ?? '-';
      },
    },
  ].filter((col) => col.visible) as TableColumn[];

  if (props.additionalTemplateColumns?.length) {
    props.additionalTemplateColumns?.forEach((each) => {
      tempColumns.splice(each.index, 0, each.column);
    });
  }

  return tempColumns;
})();

const fetchChangelogs = async (
  params?: ChangelogFilterQuery,
): Promise<FetchListResponse | undefined> => {
  try {
    const { data } = await (
      props.isEmployee
        ? ChangelogServices.getChangelogs
        : SalesDataManagerServices.getLogs
    )(params);
    return data;
  } catch (error) {
    console.error(error);
    toast.add({
      error,
      message: 'Gagal mendapatkan changelog.',
    });
  }
};
</script>

<template>
  <div v-bind="Preset?.root">
    <div v-bind="Preset?.header">
      <ButtonSearch v-bind="Preset?.buttonsearch" :table-name="tableName" />

      <ButtonFilter
        v-bind="Preset?.buttonfilter"
        :table-name="tableName"
        @show-filter="showChangelogFilter = $event"
      />

      <ButtonDownload
        v-if="props.useButtonDownload"
        v-bind="Preset?.buttondownload"
        :file-name="fileName ?? 'Changelog'"
        :table-name="tableName"
      />
    </div>

    <ChangelogFilter
      v-bind="$props"
      :additional-template-filters="additionalTemplateFilters"
      :custom-params="props.customParams"
      :default-params-query="defaultParamsQuery"
      :object="props.object"
      :object-id="defaultParamsQuery.objectId"
      :object-name-column="props.objectNameColumn"
      :removed-filters="removedFilters"
      :table-name="tableName"
    />

    <DataTable
      :key="dataTableKey"
      :columns="changelogColumn"
      :default-query-params="defaultParamsQuery"
      :fetch-function="fetchChangelogs"
      :scroll-height="scrollHeight"
      :table-name="tableName"
      :use-option="false"
      data-key="_id"
      lazy
      selection-type="none"
      use-paginator
    />
  </div>
</template>
